import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { BankReconciliation, BankReconciliationParams, ExportFormat, ReconciliationItem } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const useBankReconciliation = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const [report, setReport] = useState<BankReconciliation | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [reconcilingId, setReconcilingId] = useState<string | null>(null);
  const [params, setParams] = useState<BankReconciliationParams>({ month: currentMonth() });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await reportRepo.getBankReconciliation(params));
    } catch (e) {
      console.error('Error loading bank reconciliation:', e);
      toast.error('Error al cargar la conciliación bancaria');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(
    async (format: ExportFormat) => {
      setExporting(true);
      try {
        const blob = await reportRepo.exportBankReconciliation(format, params);
        downloadBlob(blob, generateReportFilename(`conciliacion-bancaria-${params.month}`, format));
        toast.success('Reporte exportado correctamente');
      } catch (e) {
        console.error('Error exporting bank reconciliation:', e);
        toast.error('Error al exportar el reporte');
      } finally {
        setExporting(false);
      }
    },
    [reportRepo, params, toast],
  );

  /** Mark an item as reconciled/pending, then refresh so it moves in/out of the pending lists. */
  const toggleReconciled = useCallback(
    async (item: ReconciliationItem, reconciled: boolean) => {
      setReconcilingId(item.sourceId);
      try {
        if (item.type === 'income') {
          await reportRepo.reconcilePayment(item.sourceId, reconciled);
        } else {
          await reportRepo.reconcileExpense(item.sourceId, reconciled);
        }
        await fetchReport();
      } catch (e) {
        console.error('Error toggling reconciliation:', e);
        toast.error('No se pudo actualizar la conciliación');
      } finally {
        setReconcilingId(null);
      }
    },
    [reportRepo, fetchReport, toast],
  );

  const setMonth = useCallback((month: string) => setParams((p) => ({ ...p, month })), []);
  const setManualFields = useCallback(
    (fields: Partial<BankReconciliationParams>) => setParams((p) => ({ ...p, ...fields })),
    [],
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return {
    report,
    loading,
    exporting,
    reconcilingId,
    params,
    setMonth,
    setManualFields,
    toggleReconciled,
    refetch: fetchReport,
    exportReport,
  };
};
